import { useEffect, useMemo } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { ArrowCircleLeft } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import * as Sentry from '@sentry/react';
import type { ToOptions } from '@tanstack/react-router';
import { useNavigate } from '@tanstack/react-router';
import toast from 'react-hot-toast';

import { HTTPError } from 'src/api/createApiClient.types';
import {
  useDevices,
  usePollDeviceActivationStatus,
  usePollForNewDevice,
} from 'src/api/queries';
import {
  ONE_TIME_CODE_EXPIRATION_TIME,
  ONE_TIME_CODE_TOO_MANY_REQUESTS_TIMEOUT,
} from 'src/api/queries/devices.config';
import { useOneTimeCode } from 'src/api/queries/oneTimeCode';
import {
  Button,
  Link,
  NotificationBox,
  SomethingWentWrong,
  Spinner,
} from 'src/components';
import { Link2 } from 'src/components/Link2';
import { PairingCompleteToast } from 'src/components/PairingCompleteToast';
import { URLS } from 'src/utils/urls/urls';
import { useCopyToClipboard } from 'src/utils/useCopyToClipboard';
import { useCountdown } from 'src/utils/useCountdown';

import { Select, Trans, useLingui } from '@lingui/react/macro';
import { deviceHelpers } from 'src/utils/deviceHelpers';
import {
  filterActivePaperTabletClients,
  isClientPaperTablet,
} from '../utils/utils';

interface OneTimeCodeProps {
  description: string;
  componentLocation: string;
  successRedirectUrl?: string;
  hideBack?: boolean;
  showUserInfo?: boolean;
  showLogout?: boolean;
  deviceTypeString?: string; // not for remarkable
  allowPairing: boolean;
  hideCopyToClipboard?: boolean;
}

export const OneTimeCode = ({
  description,
  successRedirectUrl,
  hideBack,
  showUserInfo,
  showLogout,
  deviceTypeString,
  allowPairing,
  hideCopyToClipboard = false,
  componentLocation,
}: OneTimeCodeProps) => {
  const { t } = useLingui();
  const auth = useAuth0();

  const countdown = useCountdown(ONE_TIME_CODE_EXPIRATION_TIME);
  const copyToClipboard = useCopyToClipboard();
  const devices = useDevices();
  const navigate = useNavigate();

  // One time code is fetched automatically on mount
  const oneTimeCode = useOneTimeCode({ componentLocation });
  // Polling starts whenever the one time code changes
  const pollForNewDevice = usePollForNewDevice(oneTimeCode.data);
  // Device activation status is polled when the new device is a remarkable device
  const deviceActivationStatus = usePollDeviceActivationStatus(
    pollForNewDevice.data
  );

  const activeDevices = useMemo(
    () => filterActivePaperTabletClients(devices.data),
    [devices.data]
  );

  const maxDeviceCount = deviceHelpers.getMaxDeviceCount(auth.user);
  const canConnectMoreDevices = activeDevices.length < maxDeviceCount;

  // ------------------ Handle errors ------------------

  const isTooManyRequests = useMemo(() => {
    if (oneTimeCode.error instanceof HTTPError) {
      return oneTimeCode.error.response.status === 429;
    }

    return false;
  }, [oneTimeCode.error]);

  // Delay refetching of one time code when too many requests
  useEffect(() => {
    if (isTooManyRequests) {
      const timeout = setTimeout(() => {
        void oneTimeCode.refetch();
      }, ONE_TIME_CODE_TOO_MANY_REQUESTS_TIMEOUT);

      return () => clearTimeout(timeout);
    }
  }, [isTooManyRequests]);

  // ------------------ Handle countdown ------------------

  // reset countdown when new code is generated
  useEffect(() => {
    countdown.reset();
  }, [oneTimeCode.data]);

  // Refetch the one time code when the countdown is finished
  useEffect(() => {
    if (pollForNewDevice.data) return; // Don't refetch when we have found a new device

    if (countdown.isFinished) {
      void oneTimeCode.refetch();
    }
  }, [countdown.isFinished, pollForNewDevice.data]);

  // ------------------ Handle pairing ------------------
  useEffect(() => {
    const newDevice = pollForNewDevice.data;
    if (!newDevice) return;

    if (!isClientPaperTablet(newDevice) && successRedirectUrl) {
      Sentry?.addBreadcrumb({
        category: 'Pairing',
        message:
          'Navigating to success redirect url after pairing non tablet device',
        data: {
          deviceType: newDevice.clientType,
        },
      });
      void navigate({ to: successRedirectUrl });
      return;
    }

    // This is now a remarkable device, so we need to make sure we have the activation status
    if (
      !deviceActivationStatus.data ||
      deviceActivationStatus.data.processed !== true
    ) {
      return;
    }

    if (deviceActivationStatus.data.connectOffer) {
      const CONNECT_OFFER_URL: ToOptions['to'] =
        URLS.STORE_CONNECT_OFFER_LANDING;

      Sentry?.addBreadcrumb({
        category: 'Pairing',
        message: `Navigating the user to ${CONNECT_OFFER_URL}`,
        data: {
          deviceType: newDevice.clientType,
        },
      });

      void navigate({ to: CONNECT_OFFER_URL });
    } else if (deviceActivationStatus.data.dmOffer) {
      const DM_OFFER_URL: ToOptions['to'] = URLS.STORE_DM_OFFER_LANDING;

      Sentry?.addBreadcrumb({
        category: 'Pairing',
        message: `Navigating the user to ${DM_OFFER_URL}`,
        data: {
          deviceType: newDevice.clientType,
        },
      });

      void navigate({ to: DM_OFFER_URL });
    } else if (successRedirectUrl) {
      Sentry?.addBreadcrumb({
        category: 'Pairing',
        message: `Navigating the user to fallback url`,
        data: {
          deviceType: newDevice.clientType,
          url: successRedirectUrl,
        },
      });

      void navigate({ to: successRedirectUrl });
    } else {
      Sentry?.addBreadcrumb({
        category: 'Pairing',
        message:
          'Pairing complete, but no offer or fallback url to navigate to.',
        data: {
          deviceType: newDevice.clientType,
        },
      });
    }
    toast.custom(PairingCompleteToast, {
      duration: 6500,
      position: 'top-right',
      id: 'pairing-complete-toast',
    });
  }, [pollForNewDevice.data, deviceActivationStatus.data]);

  // ------------------ Template ------------------

  if (devices.isPending || oneTimeCode.isPending) {
    return <Spinner />;
  }
  if (devices.isError) {
    return <SomethingWentWrong error={devices.error} />;
  }

  if (!canConnectMoreDevices && successRedirectUrl === URLS.DEVICE_REMARKABLE) {
    return (
      <div className="flex flex-col items-center">
        <NotificationBox
          variant="info"
          className="relative mx-8"
          data-cy="max-devices-paired-warning"
          title={t`Maximum devices already paired`}
        >
          <Typography
            variant="body-sm"
            className="p-0 text-center md:text-left"
          >
            <Trans>
              You have already paired the maximum number of devices.
            </Trans>
          </Typography>
          <Link2
            as="button"
            variant="secondary"
            className="mt-8"
            size="small"
            to="/device/remarkable"
            componentLocation={componentLocation}
          >
            <Trans>Manage devices</Trans>
          </Link2>
        </NotificationBox>
      </div>
    );
  }

  if (deviceTypeString && !allowPairing) {
    return (
      <div className="flex flex-col items-center">
        <NotificationBox
          variant="warning"
          className="relative mx-8"
          data-cy="connect-warning"
          title={t`Can't identify reMarkable account`}
        >
          <Typography
            variant="body-sm"
            className="p-0 text-center md:text-left"
          >
            <Trans>
              To pair the app, please use the email address that you see in
              General settings &gt; Account on your paper tablet.
            </Trans>
          </Typography>

          <div className="my-16 flex flex-col gap-24 lm:flex-row">
            <Link2
              as="button"
              variant="primary"
              className="mt-8"
              size="medium"
              to="/device/remarkable/connect"
              componentLocation={componentLocation}
            >
              <Trans>Pair device</Trans>
            </Link2>

            <Button
              variant="secondary"
              className="mt-8 ml-4"
              onClick={() => {
                void auth.logout({
                  logoutParams: {
                    returnTo: window.location.origin,
                  },
                });
              }}
            >
              <Trans>Log out</Trans>
            </Button>
          </div>
        </NotificationBox>
      </div>
    );
  }

  const userEmail = auth.user?.email ?? '';

  return (
    <div className="mb-16 flex flex-col items-center text-center">
      {hideBack ? null : (
        <div className="w-full">
          <Link2
            search={{ showOtp: false }}
            variant="tertiary-neutral"
            className="mb-12"
            componentLocation={componentLocation}
          >
            <ArrowCircleLeft size={24} />
            <span>
              <Trans>Back</Trans>
            </span>
          </Link2>
        </div>
      )}

      <Typography as="h3" variant="heading-md" className="mt-8 mb-16">
        <Trans>Verification code</Trans>
      </Typography>

      {showUserInfo && auth.user && (
        <Typography variant="body-md" className="my-16">
          <Trans>
            Logged in as
            <br />
            {userEmail}
          </Trans>
        </Typography>
      )}

      <p className="md:max-w-screen-sm">{description}</p>

      {pollForNewDevice.isSuccess &&
      !successRedirectUrl &&
      !isClientPaperTablet(pollForNewDevice.data) ? (
        <NotificationBox
          variant="success"
          title={t`App successfully paired`}
          className="my-24 w-full animate-fade-in-up"
        >
          <Trans>Go back to start using the app.</Trans>
        </NotificationBox>
      ) : (
        <>
          {deviceActivationStatus.isPending && deviceActivationStatus.data ? (
            <span
              className="mt-8 flex"
              data-cy="activation-status-loading-indicator"
            >
              <p>
                <Trans>Checking activation status...</Trans>
              </p>
            </span>
          ) : (
            <span className="mt-8 flex">
              <p className="mr-4">
                <Trans>The code is valid for</Trans>
              </p>{' '}
              <span data-cy="countdown" className="font-semibold">
                <p>{countdown.formattedTimeLeft}</p>
              </span>
            </span>
          )}
          <p
            data-cy="one-time-code"
            className="mt-24 text-60 min-[320px]:tracking-widest"
          >
            {oneTimeCode.data}
          </p>
        </>
      )}

      {isTooManyRequests ? (
        <NotificationBox
          data-cy="one-time-code-too-many-requests"
          variant="warning"
          title={t`Too many requests`}
        >
          <p>
            <Trans>
              You have generated too many codes in a short time. Please wait 10
              seconds for a new code to be generated.
            </Trans>
          </p>
        </NotificationBox>
      ) : oneTimeCode.isError ? (
        <NotificationBox
          data-cy="one-time-code-error"
          error={oneTimeCode.error}
          title={t`Could not generate new code`}
          alwaysShow
        >
          <p>
            <Trans>
              Unfortunately we could not generate a code. Please try again later
              or contact support if the problem persists.
            </Trans>
          </p>
        </NotificationBox>
      ) : (
        deviceActivationStatus.isError && (
          <NotificationBox
            data-cy="device-activation-status-warning"
            variant="warning"
            error={deviceActivationStatus.error}
            title={t`Unable to verify activation status`}
            alwaysShow
          >
            <p>
              <Trans>
                We were not able to verify activation status. Please refresh the
                page to verify that pairing was successful.
              </Trans>
            </p>
          </NotificationBox>
        )
      )}

      <Link
        as="button"
        data-cy="generate-new-code-button"
        onClick={() => oneTimeCode.refetch()}
        disabled={isTooManyRequests}
        trackingLocation={componentLocation}
      >
        <span className="underline">
          <Trans>Get new code</Trans>
        </span>
      </Link>

      {copyToClipboard.isError && (
        <NotificationBox
          error={copyToClipboard.error}
          className="relative mx-24 mt-20"
          data-cy="copy-to-clipboard-warning"
          title={t`Could not copy code to clipboard`}
          alwaysShow
        >
          <Typography
            variant="body-sm"
            className="p-0 text-center md:text-left"
          >
            <Trans>
              Unfortunately we could not copy the code to your clipboard. Please
              copy it manually.
            </Trans>
          </Typography>
        </NotificationBox>
      )}

      <div className="flex gap-16">
        {showLogout && (
          <Button
            variant="tertiary"
            className="mt-24"
            onClick={() => {
              void auth.logout({
                logoutParams: {
                  returnTo: window.location.origin + URLS.PAIR_APP,
                },
              });
            }}
          >
            <Trans>Log out</Trans>
          </Button>
        )}

        {!hideCopyToClipboard && !countdown.isFinished && oneTimeCode.data && (
          <Button
            variant="secondary"
            data-cy="copy-to-clipboard-button"
            className="mt-24"
            loading={copyToClipboard.isPending}
            onClick={() => copyToClipboard.mutate(oneTimeCode.data)}
          >
            <Select
              context="Copy one-time code to clipboard button"
              value={copyToClipboard.isSuccess ? 'copied' : 'copy'}
              _copied="Copied!"
              other="Copy to clipboard"
            />
          </Button>
        )}
      </div>
    </div>
  );
};
